import { useEffect } from 'react';
import { orderBy } from 'lodash';
import Modal from 'antd/lib/modal';
import Typography from 'antd/lib/typography';
import Timeline, { TimelineItemProps } from 'antd/lib/timeline';
import Skeleton from 'antd/lib/skeleton';
import Spin from 'antd/lib/spin';
import Empty from 'antd/lib/empty';
import App from 'antd/lib/app';
import { useAuditTrailListQuery } from '../../../domain/audit-trail';
import { AuditEntityType, IAuditTrail } from '../../../domain/audit-trail/interface';
import { MemoizedDatetimePopover } from '../../datetimePopover/datetimePopover';

interface IAuditTrailModalProps {
    onClose: Function;
    entityId: number | string;
    entityType: AuditEntityType;
    pageSize?: number;
}

export const AuditTrailModal = ({ onClose, entityId, entityType, pageSize }: IAuditTrailModalProps) => {
    const { notification } = App.useApp();

    const { data, isLoading, isFetching, isError, error } = useAuditTrailListQuery(
        {
            filter: { entity_id: entityId, entity_type: entityType },
            pagination: { pageSize: pageSize || 10, current: 1 },
            include: 'data',
        },
        {
            keepPreviousData: true,
        }
    );

    useEffect(() => {
        if (isError) {
            notification.error({
                key: 'audit-trail-fetch-error',
                message: error?.message || 'Couldn’t load audit trail data',
            });
        }
    }, [isError, error, notification]);

    const auditTrailData = data?.data?.length ? orderBy(data.data, ['datetime', 'id'], ['desc', 'desc']) : [];

    const handleClose = () => onClose();

    return (
        <Modal
            open
            destroyOnHidden
            title="Audit Trail"
            onCancel={handleClose}
            styles={{
                body: { maxHeight: '600px', overflow: 'auto' },
            }}
            footer={null}
        >
            {isLoading ? <Skeleton loading active /> : <Spin spinning={isFetching}>{renderContent(auditTrailData)}</Spin>}
        </Modal>
    );
};

const renderContent = (auditTrailData: IAuditTrail[]) => {
    if (!auditTrailData.length) {
        return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
    }

    const items: TimelineItemProps[] = [];

    auditTrailData.forEach((auditItem, index) => {
        items.push({
            key: `audit-timeline-item-${index}`,
            label: '',
            children: (
                <>
                    <Typography style={{ width: 'fit-content' }}>
                        <MemoizedDatetimePopover timestamp={new Date(auditItem.datetime).valueOf()} />
                    </Typography>

                    <Typography.Text>
                        {auditItem.actor}, {auditItem.action}
                    </Typography.Text>
                    {auditItem.data && (
                        <Typography.Paragraph code style={{ whiteSpace: 'break-spaces' }} ellipsis>
                            {JSON.stringify(auditItem.data, null, 2)}
                        </Typography.Paragraph>
                    )}
                </>
            ),
        });
    });

    return <Timeline items={items} />;
};
